import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Slide,
  TextField,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'react-toastify';

const BRAND_BLUE = '#14a9e8';
const BRAND_BLUE_HOVER = '#1298cf';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const HolidayDialog = ({ open, onClose }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'Summer Cleaning Inquiry',
    message: '',
    preferredDate: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const toastId = toast.loading('Sending your Summer Cleaning inquiry...');

    try {
      const messageWithDate = `${formData.message}\n\nPreferred Date: ${formData.preferredDate}`;
      await axios.post(`${process.env.REACT_APP_API_URL}/api/send-email`, {
        ...formData,
        message: messageWithDate,
      });

      toast.update(toastId, {
        render: 'Your Summer Cleaning inquiry has been sent!',
        type: 'success',
        isLoading: false,
        autoClose: 3000,
      });

      setLoading(false);
      setStep(3);
    } catch (err) {
      toast.update(toastId, {
        render: 'Failed to send your inquiry. Please try again later.',
        type: 'error',
        isLoading: false,
        autoClose: 3000,
      });
      setLoading(false);
    }
  };

  const primaryBtnSx = {
    backgroundColor: BRAND_BLUE,
    color: '#fff',
    '&:hover': { backgroundColor: BRAND_BLUE_HOVER },
    borderRadius: '25px',
    px: 3,
    py: 1,
    textTransform: 'none',
    fontWeight: 700,
  };

  const ghostBtnSx = {
    color: 'rgba(255,255,255,.85)',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: '25px',
    px: 2,
    '&:hover': { backgroundColor: 'rgba(255,255,255,.12)' },
  };

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      keepMounted
      onClose={onClose}
      PaperProps={{
        sx: {
          background: `linear-gradient(135deg, #0b1620 0%, #0f2940 50%, #14598a 100%)`,
          borderRadius: '16px',
          color: '#fff',
          p: 2,
          maxWidth: '600px',
          border: '1px solid rgba(255,255,255,.1)',
        },
      }}
    >
      {step === 1 && (
        <>
          <DialogContent>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: '#fff', mb: 1, textAlign: 'center' }}
            >
              🍁 Fall is here, how can we help?
            </Typography>

            <Typography
              variant="body1"
              sx={{ mb: 2, textAlign: 'center', color: 'rgba(255,255,255,.85)' }}
            >
              Fall is here, kids are in school and the leaves are already falling! You've got enough on your hands, let us handle the cleaning so you can relax!.
            </Typography>

            <Typography
              variant="body1"
              sx={{ mb: 2, textAlign: 'center', color: 'rgba(255,255,255,.85)' }}
            >
              Book a <strong>Deep Clean</strong>, <strong>Party Prep</strong>, or
              {' '}<strong>Recurring Service</strong> and enjoy family time
              while we make your house spotless!
            </Typography>

            <Typography variant="body2" sx={{ color: BRAND_BLUE, textAlign: 'center', fontWeight: 700 }}>
              🗓️ <span style={{ fontStyle: 'italic' }}>Fall and winter get full, book your cleaning now!</span>
            </Typography>
          </DialogContent>

          <DialogActions sx={{ justifyContent: 'center', pb: 2, gap: 1 }}>
            <Button variant="contained" onClick={() => setStep(2)} sx={primaryBtnSx}>
              Book My Cleaning Now
            </Button>

            <Button onClick={onClose} sx={ghostBtnSx}>
              Maybe Later
            </Button>
          </DialogActions>
        </>
      )}

      {step === 2 && (
        <DialogContent>
          <Typography
            variant="h5"
            sx={{ textAlign: 'center', mb: 2, color: '#fff', fontWeight: 800 }}
          >
            Booking Inquiry
          </Typography>

          <Typography sx={{ textAlign: 'center', mb: 2, color: 'rgba(255,255,255,.8)' }}>
            Tell us what you'd like cleaned (and when). We'll reach out shortly to confirm the details and get you on the
            schedule.
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              fullWidth
              name="name"
              label="Name"
              value={formData.name}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              InputProps={{ sx: { color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.25)' } } }}
            />
            <TextField
              fullWidth
              name="email"
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              InputProps={{ sx: { color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.25)' } } }}
            />
            <TextField
              fullWidth
              name="phone"
              label="Phone (optional)"
              value={formData.phone}
              onChange={handleChange}
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              InputProps={{ sx: { color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.25)' } } }}
            />
            <TextField
              fullWidth
              name="preferredDate"
              label="Preferred Cleaning Date"
              type="date"
              value={formData.preferredDate}
              onChange={handleChange}
              InputLabelProps={{ shrink: true, sx: { color: 'rgba(255,255,255,.6)' } }}
              required
              sx={{ mb: 2 }}
              InputProps={{ sx: { color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.25)' } } }}
            />
            <TextField
              fullWidth
              name="message"
              label="What would you like us to focus on?"
              multiline
              rows={3}
              value={formData.message}
              onChange={handleChange}
              required
              sx={{ mb: 3 }}
              placeholder="Example: Party prep for July 4th, deep clean kitchen + bathrooms, recurring summer visits…"
              InputLabelProps={{ sx: { color: 'rgba(255,255,255,.6)' } }}
              InputProps={{ sx: { color: '#fff', '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,.25)' } } }}
            />

            <Box textAlign="center">
              <Button variant="contained" type="submit" disabled={loading} sx={primaryBtnSx}>
                {loading ? 'Sending...' : 'Submit Booking Inquiry'}
              </Button>

              <Button
                onClick={() => {
                  onClose();
                  setStep(1);
                }}
                sx={{ ...ghostBtnSx, ml: 2 }}
              >
                Maybe later
              </Button>
            </Box>
          </Box>
        </DialogContent>
      )}

      {step === 3 && (
        <DialogContent>
          <Typography
            variant="h6"
            sx={{ textAlign: 'center', mb: 2, color: '#fff', fontWeight: 800 }}
          >
            ✅ Summer Clean Requested
          </Typography>

          <Typography variant="body1" sx={{ textAlign: 'center', mb: 1, color: 'rgba(255,255,255,.85)' }}>
            We've received your Summer Cleaning inquiry.
          </Typography>

          <Typography variant="body2" sx={{ textAlign: 'center', mb: 2, color: 'rgba(255,255,255,.7)' }}>
            Our team will contact you soon to confirm your cleaning date and next steps.
          </Typography>

          <Box textAlign="center">
            <Button variant="contained" onClick={onClose} sx={primaryBtnSx}>
              Close
            </Button>
          </Box>
        </DialogContent>
      )}
    </Dialog>
  );
};

export default HolidayDialog;
